// Tournament roll-call (round-29, 2026-08-31). User request: before a tournament round, the
// kiosk announces and asks each registered player in turn — "Luuk, ¿estás listo?" -> "Sí" ->
// checked in, next player — reusing the SAME machinery the corner-claim confirmation flow
// (round-27) already built: server-side neural TTS (tts_service.py, cached per player+lang),
// the yes/no grammar (kmb_stt.py), and a small idle -> awaiting -> resolved state machine
// (mirrors services/confirmFlow.ts's shape closely, but is deliberately its OWN state machine —
// a roll-call session walks a whole LIST of players, not one single ask, and its per-player
// timeout marks that player pending/absent rather than blocking or cancelling the rest of the
// queue, which confirmFlow.ts's single-ask replace-on-cancel design doesn't model).
//
// Voice is never the only path: every player's row is always tappable (Sí/No), same requirement
// as the confirmation flow. Speaker matching is used as a SANITY CHECK ONLY (never a hard block)
// — the answering voice is matched against the tournament's candidate scope
// (candidateScope.ts's 'tournament' tier: every OTHER registered participant, since the
// currently-asked player is who the question is ABOUT, not itself a competing candidate) and
// flagged (never blocked) when it clearly points at someone else. Matching still has to work
// gracefully for an UNENROLLED player answering — no candidates above the floor simply means no
// mismatch flag is raised, never a stall.
//
// Tracked per §16 via POST /api/voice/roll-call (server/app/models.py::RollCallInteraction) —
// one row per prompt/answer, grouped by a client-generated `sessionId` so "how long did the
// whole roll call take" is a single query.
import { reactive } from 'vue'
import { rollCallTtsUrl, postRollCallInteraction } from '../backend'
import { onSttCommand, spkForCommand, type SttCommandEvent, type SttLang } from '../stt'
import { rankSpeakerCandidates, classifyConfidenceBand } from '../voiceScore'
import { tournamentScope, scopedCandidates, scopeLabel } from './candidateScope'
import { getVoiceProfiles } from '../backend'
import { confirmState } from './confirmFlow'
import { shipEvent } from '../logging'
import type { Player, RollCallOutcome } from '../types'

/** ~8s — shorter than the single-ask confirmation flow's 10s (services/confirmFlow.ts): a roll
 *  call is a whole LIST, and a slow answer here costs the rest of the queue, not just one ask. */
export const ROLL_CALL_TIMEOUT_MS = 8_000

export type RollCallRowStatus = 'pending' | 'asking' | 'confirmed' | 'denied' | 'timeout' | 'skipped'

export interface RollCallRow {
  player: Player
  status: RollCallRowStatus
  /** Sanity-check flag only (see module docblock) — never gates the outcome. */
  speakerMismatch: boolean
  answerSource: 'voice' | 'tap' | null
}

interface RollCallStateShape {
  active: boolean
  tournamentId: number | null
  sessionId: string | null
  lang: SttLang
  rows: RollCallRow[]
  currentIndex: number
  /** Mirrors services/confirmFlow.ts's confirmState.status — App.vue's onSttCommand hub checks
   *  this alongside confirmState before routing a 'yes_no' command. */
  status: 'idle' | 'awaiting'
  deadlineTs: number | null
}

export const rollCallState = reactive<RollCallStateShape>({
  active: false,
  tournamentId: null,
  sessionId: null,
  lang: 'es',
  rows: [],
  currentIndex: -1,
  status: 'idle',
  deadlineTs: null,
})

let timer: ReturnType<typeof setTimeout> | null = null
let startedAt = 0
let voiceProfilesCache: { playerId: number; centroid: number[] | null | undefined; spkModelVersion?: string | null }[] | null = null
// Round-29: whether the CURRENT ask's TTS actually played — set fresh by askCurrent(), read by
// whichever of answerCurrentRollCall/skipCurrentRollCall/the timeout path resolves this ask, so
// every persisted row (not just the timeout path) carries a real ttsOk instead of a hardcoded
// guess.
let currentTtsOkPromise: Promise<boolean> = Promise.resolve(false)

function sessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function promptText(displayName: string, lang: SttLang): string {
  return lang === 'en' ? `${displayName}, are you ready?` : `${displayName}, ¿estás listo?`
}

/** Fires the roll-call TTS request, best-effort — never blocks the ask, same fail-soft contract
 *  as confirmFlow.ts's attemptConfirmTts. */
function attemptRollCallTts(playerId: number, lang: SttLang): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      resolve(ok)
    }
    try {
      const audio = new Audio(rollCallTtsUrl(playerId, lang))
      audio.addEventListener('error', () => finish(false), { once: true })
      audio.play().then(
        () => finish(true),
        () => finish(false),
      )
    } catch {
      finish(false)
    }
    setTimeout(() => finish(false), 4000)
  })
}

function persistRow(row: RollCallRow, outcome: RollCallOutcome, orderIndex: number, latencyMs: number | null, ttsOk: boolean) {
  if (!rollCallState.tournamentId || !rollCallState.sessionId) return
  postRollCallInteraction({
    sessionId: rollCallState.sessionId,
    tournamentId: rollCallState.tournamentId,
    playerId: row.player.id,
    orderIndex,
    promptText: promptText(row.player.displayName, rollCallState.lang),
    promptLang: rollCallState.lang,
    outcome,
    answerSource: row.answerSource,
    speakerMismatch: row.speakerMismatch,
    latencyMs,
    ttsAttempted: true,
    ttsOk,
  }).catch(() => {
    /* best-effort — never surfaces to the UI */
  })
  shipEvent('roll-call', {
    sessionId: rollCallState.sessionId,
    tournamentId: rollCallState.tournamentId,
    playerId: row.player.id,
    outcome,
    speakerMismatch: row.speakerMismatch,
  })
}

/** Sanity-check-only speaker match (module docblock) — never awaited by the caller's critical
 *  path, resolves in the background and patches the row's `speakerMismatch` flag after the fact
 *  if it turns out to disagree. Scope: every OTHER registered participant (candidateScope.ts's
 *  'tournament' tier) — the player being asked isn't a competing candidate for "who answered". */
async function checkSpeakerSanity(cmd: SttCommandEvent, askedPlayerId: number, row: RollCallRow) {
  const tournamentId = rollCallState.tournamentId
  if (!tournamentId) return
  const fp = spkForCommand(cmd)
  if (!fp) return
  if (!voiceProfilesCache) {
    try {
      voiceProfilesCache = (await getVoiceProfiles()).items.map((p) => ({ playerId: p.playerId, centroid: p.centroid, spkModelVersion: p.spkModelVersion }))
    } catch {
      voiceProfilesCache = []
    }
  }
  const otherIds = rollCallState.rows.map((r) => r.player.id).filter((id) => id !== askedPlayerId)
  const scope = tournamentScope(tournamentId, otherIds)
  const candidates = scopedCandidates(voiceProfilesCache, scope)
  if (candidates.length === 0) return // nobody else enrolled to contest the answer — nothing to flag
  const ranked = rankSpeakerCandidates(fp.spk, fp.spkModelVersion, candidates)
  const band = classifyConfidenceBand(ranked, fp.durationSec)
  if (band.band !== 'low' && band.top && band.top.id !== askedPlayerId) {
    row.speakerMismatch = true
    shipEvent('roll-call-speaker-mismatch', {
      sessionId: rollCallState.sessionId,
      askedPlayerId,
      matchedPlayerId: band.top.id,
      confidence: Math.round(band.top.confidence * 1000) / 1000,
      scope: scopeLabel(scope, candidates.length),
    })
  }
}

function clearTimer() {
  if (timer) clearTimeout(timer)
  timer = null
}

function askCurrent() {
  clearTimer()
  const row = rollCallState.rows[rollCallState.currentIndex]
  if (!row) {
    finishSession()
    return
  }
  row.status = 'asking'
  rollCallState.status = 'awaiting'
  startedAt = Date.now()
  rollCallState.deadlineTs = startedAt + ROLL_CALL_TIMEOUT_MS
  currentTtsOkPromise = attemptRollCallTts(row.player.id, rollCallState.lang)
  const orderIndex = rollCallState.currentIndex
  timer = setTimeout(() => {
    // Capture BEFORE advance() — advancing to the next row reassigns currentTtsOkPromise, so the
    // reference must be grabbed first or this row's persisted outcome would carry the WRONG
    // (next row's) ttsOk.
    const ttsPromise = currentTtsOkPromise
    row.status = 'timeout'
    advance()
    void ttsPromise.then((ttsOk) => persistRow(row, 'timeout', orderIndex, ROLL_CALL_TIMEOUT_MS, ttsOk))
  }, ROLL_CALL_TIMEOUT_MS)
}

function advance() {
  clearTimer()
  rollCallState.status = 'idle'
  rollCallState.deadlineTs = null
  const next = rollCallState.currentIndex + 1
  if (next >= rollCallState.rows.length) {
    finishSession()
    return
  }
  rollCallState.currentIndex = next
  askCurrent()
}

function finishSession() {
  clearTimer()
  rollCallState.status = 'idle'
  rollCallState.deadlineTs = null
  rollCallState.currentIndex = -1
  // `active` stays true — RollCallPanel.vue shows the final tally until the user closes it
  // (closeRollCall). No further asks fire once currentIndex is -1.
}

export function startRollCall(tournamentId: number, players: Player[], lang: SttLang = 'es') {
  if (players.length === 0) return
  clearTimer()
  voiceProfilesCache = null
  rollCallState.active = true
  rollCallState.tournamentId = tournamentId
  rollCallState.sessionId = sessionId()
  rollCallState.lang = lang
  rollCallState.rows = players.map((player) => ({ player, status: 'pending' as RollCallRowStatus, speakerMismatch: false, answerSource: null }))
  rollCallState.currentIndex = 0
  askCurrent()
}

export function closeRollCall() {
  clearTimer()
  rollCallState.active = false
  rollCallState.tournamentId = null
  rollCallState.sessionId = null
  rollCallState.rows = []
  rollCallState.currentIndex = -1
  rollCallState.status = 'idle'
  rollCallState.deadlineTs = null
}

/** Answers the CURRENT row, voice or tap. No-op unless rollCallState.status === 'awaiting'. UI
 *  advances immediately (same responsiveness convention as confirmFlow.ts); the observability
 *  POST fires in the background once BOTH the TTS-played check and the (never-blocking) speaker-
 *  match sanity check settle, so the persisted row's `speakerMismatch`/`ttsOk` are accurate
 *  rather than a synchronous guess. */
export function answerCurrentRollCall(cmd: SttCommandEvent | null, answer: 'yes' | 'no', source: 'voice' | 'tap') {
  if (rollCallState.status !== 'awaiting') return
  const row = rollCallState.rows[rollCallState.currentIndex]
  if (!row) return
  const ttsPromise = currentTtsOkPromise // capture before advance() reassigns it — see askCurrent
  clearTimer()
  const latencyMs = Date.now() - startedAt
  const outcome: RollCallOutcome = answer === 'yes' ? 'confirmed' : 'denied'
  row.status = outcome
  row.answerSource = source
  const orderIndex = rollCallState.currentIndex
  advance()
  const sanityPromise = cmd ? checkSpeakerSanity(cmd, row.player.id, row) : Promise.resolve()
  void Promise.all([ttsPromise, sanityPromise]).then(([ttsOk]) => persistRow(row, outcome, orderIndex, latencyMs, ttsOk))
}

/** "Skip" tap — moves on without a yes/no answer, e.g. a player who stepped away. */
export function skipCurrentRollCall() {
  if (rollCallState.status !== 'awaiting') return
  const row = rollCallState.rows[rollCallState.currentIndex]
  if (!row) return
  const ttsPromise = currentTtsOkPromise
  clearTimer()
  row.status = 'skipped'
  const orderIndex = rollCallState.currentIndex
  advance()
  void ttsPromise.then((ttsOk) => persistRow(row, 'skipped', orderIndex, null, ttsOk))
}

/** Cancels the WHOLE session (e.g. navigating away) — every row still 'pending'/'asking' is
 *  recorded as 'cancelled' for the observability trail, matching confirmFlow.ts's convention of
 *  always writing a terminal row rather than leaving an ask silently unresolved. */
export function cancelRollCall() {
  if (!rollCallState.active) return
  clearTimer()
  for (let i = rollCallState.currentIndex; i >= 0 && i < rollCallState.rows.length; i++) {
    const row = rollCallState.rows[i]
    if (row.status === 'pending' || row.status === 'asking') persistRow(row, 'cancelled', i, null, false)
  }
  closeRollCall()
}

// Subscribed once, module-level (this module is imported the moment RollCallPanel.vue mounts,
// same lifetime as the rest of the app's onSttCommand listeners). A bare 'yes_no' is classified
// stateless server-side — this listener only ACTS while a roll-call ask is actually pending, and
// explicitly defers to confirmFlow.ts's confirmState if THAT happens to be awaiting at the same
// time (corner/point-claim confirmation takes priority — in practice the two screens don't
// overlap, this is a defensive guard against the two state machines ever double-firing the same
// "sí" if they somehow were both awaiting at once).
onSttCommand((cmd) => {
  if (cmd.command !== 'yes_no') return
  if (confirmState.status === 'awaiting') return
  if (rollCallState.status !== 'awaiting') return
  answerCurrentRollCall(cmd, cmd.answer === 'no' ? 'no' : 'yes', 'voice')
})
